import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import useWs from '../lib/useWs';
import { fmtAED, fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, XCircle, Bell, RefreshCw, Bike } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const CHANNEL_LABELS = { talabat: 'Talabat', deliveroo: 'Deliveroo', careem: 'Careem', noonfood: 'Noon Food', keeta: 'Keeta', smiles: 'Smiles' };
const statusBadge = (s) => s === 'accepted' ? <Badge className="bg-success text-white">Accepted</Badge>
  : s === 'rejected' ? <Badge variant="destructive">Rejected</Badge>
  : <Badge className="bg-amber-500 text-white">New</Badge>;

export default function AggregatorInbox() {
  const { tenant } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('new');
  const [busy, setBusy] = useState('');

  const load = useCallback(async (t = tab) => {
    const { data } = await api.get('/aggregator/inbox', { params: t === 'all' ? {} : { status: t } });
    setOrders(data);
  }, [tab]);
  useEffect(() => { load(); }, [tab]);

  useWs(tenant?.id, useCallback((msg) => {
    if (msg.type === 'aggregator_inbox_new') {
      toast(`New ${CHANNEL_LABELS[msg.channel] || msg.channel} order arrived`, { icon: <Bell className="h-4 w-4" /> });
      try { new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=').play().catch(() => {}); } catch (e) {}
      load('new');
    }
    if (msg.type === 'order_new') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]));

  const accept = async (o) => {
    setBusy(o.id);
    try {
      const { data } = await api.post(`/aggregator/inbox/${o.id}/accept`);
      toast.success(`Accepted → ${data.order_no} · ${fmtAED(data.grand_total)} · KOT ${data.kot_no}`);
      if (data.missing_codes?.length) toast.warning(`Unknown item codes: ${data.missing_codes.join(', ')}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Accept failed'); }
    finally { setBusy(''); }
  };
  const reject = async (o) => {
    const reason = window.prompt('Reject reason (optional):', '') ?? '';
    setBusy(o.id);
    try { await api.post(`/aggregator/inbox/${o.id}/reject`, { reason }); toast.success('Rejected'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(''); }
  };

  const newCount = tab === 'new' ? orders.length : null;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Bike className="h-6 w-6" /> Aggregator Orders</h1>
          <p className="text-sm text-muted-foreground">Incoming app orders (Keeta, Noon, Careem, Smiles…) — accept to send them to the kitchen</p>
        </div>
        <Button variant="outline" size="sm" data-testid="inbox-refresh" onClick={() => load()}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>
      <PageHelp id="aggregator-inbox">Orders pushed by the connector agent or middleware land here. <b>Accept</b> creates the POS order, prints the KOT and deducts stock using your channel prices. <b>Reject</b> hides it (audited). Accounts with auto-accept skip this inbox entirely.</PageHelp>

      <div className="flex gap-1.5" data-testid="inbox-tabs">
        {[['new', `New${newCount != null ? ` (${newCount})` : ''}`], ['accepted', 'Accepted'], ['rejected', 'Rejected'], ['all', 'All']].map(([v, l]) => (
          <button key={v} data-testid={`inbox-tab-${v}`} onClick={() => setTab(v)}
            className={`px-3.5 py-1.5 rounded-full text-sm border ${tab === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-2" data-testid="inbox-list">
        {orders.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground" data-testid="inbox-empty">
            <Bike className="h-8 w-8 mx-auto mb-2 opacity-40" />
            {tab === 'new' ? 'No new aggregator orders — you\'re all caught up' : 'Nothing here yet'}
          </Card>
        )}
        {orders.map((o) => (
          <Card key={o.id} className="p-4 space-y-2" data-testid={`inbox-order-${o.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="capitalize">{CHANNEL_LABELS[o.channel] || o.channel}</Badge>
              <span className="font-mono font-semibold" data-testid={`inbox-ext-${o.id}`}>#{o.external_order_no}</span>
              {statusBadge(o.status)}
              <span className="text-xs text-muted-foreground ml-auto">{fmtDate(o.received_at)}</span>
            </div>
            <div className="text-sm">
              {(o.items || []).map((it, i) => <span key={i} className="inline-block mr-2">{it.qty} × <span className="font-mono">{it.code}</span></span>)}
            </div>
            {(o.customer_name || o.delivery_address || o.notes) && (
              <div className="text-xs text-muted-foreground">
                {o.customer_name && <span>{o.customer_name} {o.customer_phone} · </span>}
                {o.delivery_address && <span>{o.delivery_address} · </span>}
                {o.notes && <span>Note: {o.notes}</span>}
              </div>
            )}
            {o.accept_error && <div className="text-xs text-destructive">Auto-accept failed earlier: {o.accept_error}</div>}
            {o.status === 'new' ? (
              <div className="flex gap-2 pt-1">
                <Button size="sm" data-testid={`inbox-accept-${o.id}`} disabled={busy === o.id} onClick={() => accept(o)}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Accept → Kitchen
                </Button>
                <Button size="sm" variant="outline" data-testid={`inbox-reject-${o.id}`} disabled={busy === o.id} onClick={() => reject(o)}>
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            ) : o.status === 'accepted' && o.accepted_order_id ? (
              <div className="text-xs text-muted-foreground">POS order: <span className="font-mono">{o.accepted_order_id.slice(0, 8)}…</span>{o.accepted_by ? ` · by ${o.accepted_by}` : ''}{o.auto ? ' · auto' : ''}</div>
            ) : null}
          </Card>
        ))}
      </div>
    </div>
  );
}
